import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Database, Car } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

export default function AdminMakes() {
  const { user: me } = useAuth();
  const canEdit = ["platform_owner", "master_data_admin"].includes(me?.admin_role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", active: true });

  const load = () => {
    setLoading(true);
    const params = q ? { q } : {};
    api.get("/admin/master-data/makes", { params })
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
     
  }, [q]);

  const openNew = () => {
    setEditing(null);
    setForm({ name: "", active: true });
    setOpen(true);
  };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ name: m.name, active: !!m.active });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    try {
      if (editing) {
        await api.patch(`/admin/master-data/makes/${editing.id}`, form);
        toast.success("Make updated");
      } else {
        await api.post("/admin/master-data/makes", form);
        toast.success("Make created");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (m) => {
    if (!canEdit) return;
    // Master-data admins go through the approval workflow; PO/PA can delete directly.
    const isApprover = ["platform_owner", "platform_admin"].includes(me?.admin_role);
    if (!isApprover) {
      if (!window.confirm(`Submit a change request to delete "${m.name}"? A Platform Owner must approve before it goes live.`)) return;
      try {
        await api.post("/admin/change-requests", {
          entity_type: "make",
          action: "delete",
          target_id: m.id,
          justification: `Requested deletion of make "${m.name}"`,
        });
        toast.success("Delete request submitted for approval");
      } catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    if (!window.confirm(`Delete ${m.name}? This will also remove all its models.`)) return;
    try {
      await api.delete(`/admin/master-data/makes/${m.id}`);
      toast.success("Make deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="admin-makes-root">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] font-semibold text-[#e63946] mb-1">Master Data</div>
          <h1 className="text-[26px] md:text-[30px] font-bold text-[#1d3557] flex items-center gap-2">
            <Database size={22} className="text-[#457b9d]" /> Vehicle Makes
          </h1>
          <p className="text-[13px] text-[#667085] mt-0.5">
            {items.length} make(s) available to garages. New garages use these when adding vehicles.
          </p>
        </div>
        {canEdit && (
          <Button
            onClick={openNew}
            data-testid="admin-makes-new"
            className="h-9 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold text-[13px] px-3"
          >
            <Plus size={14} className="mr-1.5" /> New make
          </Button>
        )}
      </div>

      <Input
        data-testid="admin-makes-search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search makes…"
        className="max-w-md h-9 rounded-md text-[13px]"
      />

      <div className="bg-white border border-[#E4E7EC] rounded-lg overflow-hidden" data-testid="admin-makes-table">
        <table className="w-full text-[13px]">
          <thead className="bg-[#F7F9FC] text-[11px] uppercase tracking-wider text-[#667085]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Make</th>
              <th className="text-right px-4 py-3 font-semibold">Models</th>
              <th className="text-left px-4 py-3 font-semibold">Status</th>
              {canEdit && <th className="text-right px-4 py-3 font-semibold">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F1FAEE]">
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-12 text-center text-[13px] text-[#667085]">No makes found.</td></tr>
            )}
            {items.map((m) => (
              <tr key={m.id} data-testid={`admin-make-row-${m.id}`} className="hover:bg-[#F7F9FC]">
                <td className="px-4 py-3">
                  <div className="text-[13px] font-semibold text-[#1d3557] flex items-center gap-2">
                    <Car size={13} className="text-[#457b9d]" /> {m.name}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Link
                    to={`/admin/master-data/models?make=${m.id}`}
                    data-testid={`admin-make-models-${m.id}`}
                    className="text-[12px] text-[#457b9d] hover:text-[#e63946] font-mono font-semibold"
                  >
                    {m.model_count ?? 0} →
                  </Link>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                    m.active ? "bg-[#12B76A]/10 text-[#12B76A]" : "bg-[#66708515] text-[#667085]"
                  }`}>
                    {m.active ? "Active" : "Hidden"}
                  </span>
                </td>
                {canEdit && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-3">
                      <button
                        onClick={() => openEdit(m)}
                        data-testid={`admin-make-edit-${m.id}`}
                        className="text-[12px] text-[#457b9d] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <Pencil size={11} /> Edit
                      </button>
                      <button
                        onClick={() => remove(m)}
                        data-testid={`admin-make-delete-${m.id}`}
                        className="text-[12px] text-[#e63946] hover:underline font-semibold inline-flex items-center gap-1"
                      >
                        <Trash2 size={11} /> Delete
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-lg border-[#E4E7EC] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#1d3557] font-semibold">
              {editing ? "Edit make" : "New vehicle make"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-[#667085] font-semibold">Name</Label>
              <Input
                data-testid="admin-make-name-input"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 rounded-md"
                placeholder="e.g. Skoda"
              />
            </div>
            <label className="flex items-center gap-2 text-[13px] text-[#1d3557] cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                data-testid="admin-make-active"
                className="accent-[#e63946]"
              />
              Visible to garages
            </label>
            <Button
              onClick={save}
              data-testid="admin-make-save"
              className="w-full h-10 bg-[#e63946] hover:bg-[#d02f3c] rounded-md text-white font-semibold"
            >
              {editing ? "Save changes" : "Create make"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
